import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import {
  selectDeviceListColumnVisibility,
} from '../../../../../redux/slices/filteredDeviceListSlice/filteredDeviceListSlice';
import styles from './DeviceListTableRow.module.css';
import DevicePushStatusDisplay from '../DevicePushStatusDisplay';
import { selectAllTagsByTagIdList } from '../../../../../redux/slices/tagListSlice/tagListSlice';
import DeviceListTableRowOverlay from './DeviceListTableRowOverlay/DeviceListTableRowOverlay';
import DeviceRaLinkStatusDisplay from '../DeviceRaLinkStatusDisplay';
import { RaaRoles } from '../../../../../utility/SecurityRules';
import HasRolePrivileges from '../../../../highorder/HasRolePrivileges';
import { selectSessionInformation } from '../../../../../redux/slices/sessionSlice';

/* istanbul ignore next */
function DeviceListTableRow({
  device,
  index,
  hoveredIndex,
  setHoveredIndex,
  isSelected,
  handleSetSelected,
  disableQuickActions,
}) {
  const deviceListColumnVisibility = useSelector(selectDeviceListColumnVisibility);
  const history = useHistory();
  const { timezone } = useSelector(selectSessionInformation);
  // eslint-disable-next-line react/prop-types
  const tags = useSelector((state) => selectAllTagsByTagIdList(state, device.tagIds));
  const combinedTags = tags
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((tag) => tag.name)
    .join(', ');

  const getHoveredStyle = () => (hoveredIndex === index ? `row-hovered avt-table-row ${styles.tableRow}` : `avt-table-row ${styles.tableRow}`);
  const redirectToDevice = () => history.push({
    pathname: `/s/devices/${device.id}`,
    state: {
      latestFirmware: device.latestFirmware,
    },
  });

  const lastMessage = device.lastMessage
    ? moment.unix(device.lastMessage)
      .tz(timezone)
      .format('MMMM D, YYYY, h:mm A')
    : 'N/A';

  function compareFirmwareVersions(version1, version2) {
    if (!version1 || !version2) {
      return true;
    }
    const cleanedVersion1 = version1.replace(/[^0-9.]/g, '');
    const cleanedVersion2 = version2.replace(/[^0-9.]/g, '');

    return cleanedVersion1 === cleanedVersion2;
  }

  return (
    <tr
      style={{ backgroundColor: isSelected ? '#9fc5f8' : '', cursor: 'pointer' }}
      className={getHoveredStyle()}
      onMouseEnter={() => setHoveredIndex(index)}
      onMouseLeave={() => setHoveredIndex(null)}
    >
      <HasRolePrivileges targetRoleList={[RaaRoles.ADMIN, RaaRoles.MANAGER]}>
        <td id="device-checkbox-col" style={{ paddingLeft: '0.75rem', paddingRight: '1.0rem' }}>
          <span>
            <input
              type="checkbox"
              id={`${device.deviceName}-checkbox`}
              name={device.deviceName}
              checked={isSelected}
              onChange={() => handleSetSelected()}
            />
          </span>
        </td>
      </HasRolePrivileges>

      {deviceListColumnVisibility['Push Status'] && (
      <td onClick={redirectToDevice} className="ellipsis width-10">
        <DevicePushStatusDisplay isPushing={device.pushStatus} />
      </td>
      )}

      {deviceListColumnVisibility['Device Name'] && (
      <td onClick={redirectToDevice} className="ellipsis width-10" title={device.deviceName}>
        <span>
          <a href={`/s/devices/${device.id}`} style={{ cursor: 'pointer' }} title={device.deviceName} aria-label={device.deviceName}>
            {device.deviceName}
          </a>
        </span>
      </td>
      )}

      {deviceListColumnVisibility.Tags && (
      <td onClick={redirectToDevice} className="ellipsis width-10" title={combinedTags}>
        <span>
          {combinedTags}
        </span>
      </td>
      )}

      {deviceListColumnVisibility.Model && (
      <td onClick={redirectToDevice} className="ellipsis width-15" title={device.roomAlertTypeDisplayString}>
        <span>
          {device.roomAlertTypeDisplayString}
        </span>
      </td>
      )}

      {deviceListColumnVisibility['MAC Address'] && (
      <td onClick={redirectToDevice} className="ellipsis width-15" title={device.macAddress}>
        <span>
          {device.macAddress}
        </span>
      </td>
      )}
      {deviceListColumnVisibility['Last Message'] && (
      <td onClick={redirectToDevice} className="ellipsis width-15" title={lastMessage}>
        <span>
          {lastMessage}
        </span>
      </td>
      )}

      {deviceListColumnVisibility['Room Alert Link'] && (
      <td className="ellipsis width-15">
        <span>
          <DeviceRaLinkStatusDisplay raLinkId={device.linkId} />
        </span>
      </td>
      )}

      {deviceListColumnVisibility.Firmware && (
      <td onClick={redirectToDevice} className="ellipsis width-10" title={device.firmwareVersion}>
        <span>
          {!compareFirmwareVersions(device.firmwareVersion, device.latestFirmware)
          && (
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-muted" title={`Newer version ${device.latestFirmware} available`} />
          )}
          {' '}
          <>{device.firmwareVersion}</>
        </span>
      </td>
      )}

      {!disableQuickActions && hoveredIndex === index && (
        <HasRolePrivileges targetRoleList={[RaaRoles.ADMIN, RaaRoles.MANAGER]}>
          <DeviceListTableRowOverlay
            index={index}
            deviceId={device.id}
            deviceName={device.deviceName}
          />
        </HasRolePrivileges>
      )}
    </tr>
  );
}

DeviceListTableRow.propTypes = {
  device: PropTypes.shape({
    id: PropTypes.number.isRequired,
    deviceName: PropTypes.string.isRequired,
    roomAlertTypeDisplayString: PropTypes.string.isRequired,
    macAddress: PropTypes.string.isRequired,
    lastMessage: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.oneOf([null]),
    ]),
    firmwareVersion: PropTypes.string.isRequired,
    linkId: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.oneOf([null]),
    ]),
    pushStatus: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.oneOf([null]),
    ]),
    latestFirmware: PropTypes.string.isRequired,
    suspended: PropTypes.bool.isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
  hoveredIndex: PropTypes.number.isRequired,
  setHoveredIndex: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
  handleSetSelected: PropTypes.bool.isRequired,
  disableQuickActions: PropTypes.bool.isRequired,
};

export default DeviceListTableRow;
